import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Edit2, Check, Eye, Settings, BarChart2, List, Save,
  RotateCcw, ShieldCheck, UserCheck, AlertCircle, TrendingUp, Clock,
  Users, IndianRupee, Inbox, Loader2, Trash2, CreditCard, Search, FileText, X
} from "lucide-react";
import { NotificationContext } from "../../context/NotificationContext";
import api from "../../api/axios";

export default function SubscriptionManagement() {
  const { addNotification } = useContext(NotificationContext);
  const [activeTab, setActiveTab] = useState("plans");
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Global Settings State
  const [globalSettings, setGlobalSettings] = useState({
    trial_duration_days: 365,
    auto_expire_trial: true,
    grace_period_days: 3,
    allow_downgrade: true,
    allow_upgrade_anytime: true,
    auto_renew_enabled: false,
    default_gst_percent: 18.0,
    max_staff_for_trial: 2
  });

  // Monitoring State
  const [stats, setStats] = useState({
    total_active: 0,
    total_expired: 0,
    expiring_soon: 0,
    plan_breakdown: []
  });

  // Payments State
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [paymentSearchQuery, setPaymentSearchQuery] = useState("");
  const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);

  const [editForm, setEditForm] = useState({
    name: "",
    price: "",
    duration_days: "",
    max_staff_users: "",
    invoice_limit: "",
    product_limit: "",
    customer_limit: "",
    description: "",
    features: "",
  });

  const [createForm, setCreateForm] = useState({
    name: "",
    price: "",
    duration_days: "",
    max_staff_users: "",
    invoice_limit: "",
    product_limit: "",
    customer_limit: "",
    description: "",
    features: "",
  });

  useEffect(() => {
    if (activeTab === "plans") {
      fetchPlans();
    } else if (activeTab === "settings") {
      fetchGlobalSettings();
    } else if (activeTab === "monitoring") {
      fetchStats();
    } else if (activeTab === "payments") {
      fetchPayments();
    }
  }, [activeTab]);

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true);
      const response = await api.get("/api/subscriptions/payments/");
      setPayments(response.data);
    } catch (error) {
      addNotification("Failed to fetch payments history", "error");
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/subscriptions/plans/");
      setPlans(response.data);
    } catch (error) {
      addNotification("Failed to fetch plans", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchGlobalSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/subscriptions/settings/current/");
      setGlobalSettings(response.data);
    } catch (error) {
      addNotification("Failed to fetch settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/subscriptions/stats/");
      setStats(response.data);
    } catch (error) {
      addNotification("Failed to fetch stats", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGlobalSettings = async () => {
    try {
      const payload = {
        ...globalSettings,
        trial_duration_days: parseInt(globalSettings.trial_duration_days) || 7
      };
      await api.patch("/api/subscriptions/settings/current/", payload);
      addNotification("Global settings updated!", "success");
      fetchGlobalSettings(); // Refresh to ensure sync and clean values
    } catch (error) {
      addNotification("Update failed", "error");
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan? This action cannot be undone.")) return;
    
    try {
      setLoading(true);
      await api.delete(`/api/subscriptions/plans/${planId}/`);
      addNotification("Plan deleted successfully", "success");
      fetchPlans();
    } catch (error) {
      addNotification(error.response?.data?.error || "Failed to delete plan", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setEditForm({
      name: plan.name,
      price: plan.price,
      duration_days: plan.duration_days,
      max_staff_users: plan.max_staff_users,
      invoice_limit: plan.invoice_limit,
      product_limit: plan.product_limit || "Unlimited",
      customer_limit: plan.customer_limit || "Unlimited",
      description: plan.description,
      features: Array.isArray(plan.features) ? [...new Set(plan.features)].join("\n") : "",
    });
    setShowEditModal(true);
  };

  const handleUpdatePlan = async () => {
    try {
      const payload = { 
        ...editForm, 
        price: parseFloat(editForm.price), 
        duration_days: parseInt(editForm.duration_days), 
        max_staff_users: parseInt(editForm.max_staff_users),
        features: editForm.features.split("\n").map(f => f.trim()).filter(f => f !== "")
      };
      await api.patch(`/api/subscriptions/plans/${editingPlan.id}/`, payload);
      addNotification("Plan updated!", "success");
      setShowEditModal(false);
      fetchPlans();
    } catch (error) {
      addNotification("Error updating plan", "error");
    }
  };

  const handleCreatePlan = async () => {
    try {
      const generatedCode = createForm.name.toLowerCase().replace(/\s+/g, '_') + '_' + Math.floor(Math.random() * 1000);
      const payload = {
        ...createForm,
        code: generatedCode,
        price: parseFloat(createForm.price) || 0,
        duration_days: parseInt(createForm.duration_days) || 30,
        max_staff_users: parseInt(createForm.max_staff_users) || 0,
        business_limit: "1",
        branch_limit: "1",
        features: createForm.features.split("\n").map(f => f.trim()).filter(f => f !== "")
      };
      await api.post("/api/subscriptions/plans/", payload);
      addNotification("Plan created!", "success");
      setShowCreateModal(false);
      fetchPlans();
    } catch (error) {
      addNotification("Error creating plan", "error");
    }
  };

  const displayedPlans = plans.filter(plan => {
    if (!plan.is_active) return false;
    return billingCycle === 'monthly' ? plan.duration_days < 365 : plan.duration_days >= 365;
  });

  return (
    <div className="space-y-6">
      {/* Header & Tabs */}
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">Subscriptions</h2>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Manage plans, global settings, and track performance</p>
          </div>

          {activeTab === 'plans' && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold shadow-lg shadow-purple-500/20 transition-all"
            >
              <Plus className="w-5 h-5" />
              New Plan
            </motion.button>
          )}
        </div>

        {/* Tab Switcher */}
        <div className="flex p-1.5 bg-slate-100 dark:bg-slate-800/50 backdrop-blur-md rounded-2xl border border-slate-200 dark:border-slate-700 w-fit">
          {[
            { id: 'plans', label: 'Plans', icon: List },
            { id: 'settings', label: 'Settings', icon: Settings },
            { id: 'monitoring', label: 'Monitoring', icon: BarChart2 },
            { id: 'payments', label: 'Payments', icon: CreditCard }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === tab.id
                ? "bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-400 shadow-sm border border-slate-200 dark:border-slate-600"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200"
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'plans' && (
          <motion.div
            key="plans"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Cycle Toggle */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-slate-500 dark:text-slate-400">Billing Cycle:</span>
              <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
                <button
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${billingCycle === "monthly" ? "bg-white dark:bg-slate-700 text-purple-600 shadow-sm" : "text-slate-500"}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle("yearly")}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${billingCycle === "yearly" ? "bg-white dark:bg-slate-700 text-purple-600 shadow-sm" : "text-slate-500"}`}
                >
                  Yearly
                </button>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-64 bg-slate-100 dark:bg-slate-800 animate-pulse rounded-2xl border border-slate-200 dark:border-slate-700" />
                ))}
              </div>
            ) : displayedPlans.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {displayedPlans.map((plan) => (
                  <motion.div
                    key={plan.id}
                    whileHover={{ y: -5 }}
                    className="p-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm hover:shadow-xl transition-all group"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-purple-600 transition-colors">{plan.name}</h3>
                      <span className="px-2 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold rounded-full uppercase tracking-wider">Active</span>
                    </div>

                    <div className="mb-6">
                      <span className="text-3xl font-black text-slate-900 dark:text-white">
                        {parseFloat(plan.price) === 0 ? "Free" : `₹${plan.price}`}
                      </span>
                      <span className="text-slate-500 ml-1 text-sm font-semibold">
                        {parseFloat(plan.price) === 0 
                          ? " / 1 Year" 
                          : `/${plan.duration_days >= 365 ? "yr" : "mo"}`}
                      </span>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Users className="w-4 h-4 text-purple-500" />
                        <span>{plan.max_staff_users === 0 ? "Unlimited" : `${plan.max_staff_users} Staff Members`}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span>{plan.invoice_limit} Invoices</span>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <button onClick={() => setSelectedPlanDetails(plan)} className="flex-1 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-bold hover:bg-slate-200">View</button>
                      <button onClick={() => handleEdit(plan)} className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(plan.id)} className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-lg hover:bg-red-100 transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-20 bg-slate-50 dark:bg-slate-900/30 rounded-3xl border-2 border-dashed border-slate-200 dark:border-slate-700"
              >
                <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full mb-4">
                  <Inbox className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">No plans found</h3>
                <p className="text-slate-500 dark:text-slate-400 mt-2 mb-6 max-w-sm text-center">
                  There are no {billingCycle} plans available. Please create a new plan or seed default plans from the backend.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-xl font-bold shadow-lg shadow-purple-500/20"
                  >
                    <Plus className="w-4 h-4" />
                    Create First Plan
                  </button>
                  <button
                    onClick={fetchPlans}
                    className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-white border border-slate-200 dark:border-slate-700 rounded-xl font-bold"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Retry Sync
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {activeTab === 'settings' && (
          <motion.div
            key="settings"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="max-w-4xl space-y-8"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Trial Settings */}
              <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-500/10 rounded-lg"><Clock className="w-5 h-5 text-blue-500" /></div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Trial Management</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-2 block">Trial Duration (Days)</label>
                    <input
                      type="number"
                      value={globalSettings.trial_duration_days}
                      onChange={e => setGlobalSettings({ ...globalSettings, trial_duration_days: e.target.value })}
                      className="w-full bg-slate-50 dark:bg-slate-900 border-none rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Auto-expire after trial</span>
                    <button
                      onClick={() => setGlobalSettings({ ...globalSettings, auto_expire_trial: !globalSettings.auto_expire_trial })}
                      className={`w-12 h-6 rounded-full p-1 transition-colors ${globalSettings.auto_expire_trial ? 'bg-purple-600' : 'bg-slate-300'}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${globalSettings.auto_expire_trial ? 'translate-x-6' : ''}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Billing Rules */}
              <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-emerald-500/10 rounded-lg"><IndianRupee className="w-5 h-5 text-emerald-500" /></div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">Platform Rules</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Allow Downgrade</span>
                    <button
                      onClick={() => setGlobalSettings({ ...globalSettings, allow_downgrade: !globalSettings.allow_downgrade })}
                      className={`w-12 h-6 rounded-full p-1 transition-colors ${globalSettings.allow_downgrade ? 'bg-purple-600' : 'bg-slate-300'}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${globalSettings.allow_downgrade ? 'translate-x-6' : ''}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Allow Upgrade Anytime</span>
                    <button
                      onClick={() => setGlobalSettings({ ...globalSettings, allow_upgrade_anytime: !globalSettings.allow_upgrade_anytime })}
                      className={`w-12 h-6 rounded-full p-1 transition-colors ${globalSettings.allow_upgrade_anytime ? 'bg-purple-600' : 'bg-slate-300'}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform ${globalSettings.allow_upgrade_anytime ? 'translate-x-6' : ''}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={handleUpdateGlobalSettings}
                className="flex items-center gap-2 px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-purple-500/20"
              >
                <Save className="w-5 h-5" />
                Save Changes
              </button>
            </div>
          </motion.div>
        )}

        {activeTab === 'monitoring' && (
          <motion.div
            key="monitoring"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-8"
          >
            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { label: 'Active Users', value: stats.total_active, icon: UserCheck, color: 'text-emerald-500' },
                { label: 'Expired', value: stats.total_expired, icon: AlertCircle, color: 'text-rose-500' },
                { label: 'Expiring Soon', value: stats.expiring_soon, icon: Clock, color: 'text-amber-500' }
              ].map((stat, i) => (
                <div key={i} className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden relative">
                  <div className="relative z-10">
                    <stat.icon className={`w-8 h-8 ${stat.color} mb-4`} />
                    <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{stat.label}</p>
                    <h4 className="text-4xl font-black mt-1 text-slate-900 dark:text-white">{stat.value}</h4>
                  </div>
                  <div className="absolute top-0 right-0 p-4 opacity-5">
                    <stat.icon className="w-24 h-24" />
                  </div>
                </div>
              ))}
            </div>

            {/* Plan Distribution */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="flex items-center gap-3 mb-8">
                <TrendingUp className="w-6 h-6 text-purple-600" />
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Plan Distribution</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.plan_breakdown.map((plan, i) => (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between items-end">
                      <span className="text-sm font-bold text-slate-600 dark:text-slate-400">{plan.plan__name}</span>
                      <span className="text-lg font-black text-slate-900 dark:text-white">{plan.count}</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(plan.count / stats.total_active) * 100}%` }}
                        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'payments' && (
          <motion.div
            key="payments"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Filters Row */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                {[
                  { id: 'all', label: 'All Statuses' },
                  { id: 'completed', label: 'Successful' },
                  { id: 'pending', label: 'Pending' },
                  { id: 'failed', label: 'Failed' }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setPaymentStatusFilter(filter.id)}
                    className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                      paymentStatusFilter === filter.id
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-650'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Search input */}
              <div className="relative max-w-sm w-full">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="w-4 h-4 text-slate-400" />
                </span>
                <input
                  type="text"
                  placeholder="Search by User, Email, ID..."
                  value={paymentSearchQuery}
                  onChange={(e) => setPaymentSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold w-full focus:ring-2 focus:ring-purple-500 focus:outline-none focus:bg-white transition-all text-slate-800 dark:text-white"
                />
              </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm overflow-hidden">
              {paymentsLoading ? (
                <div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  <span className="text-sm font-bold">Loading payment records...</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-450 text-[10px] font-black uppercase tracking-wider">
                        <th className="px-6 py-4">User</th>
                        <th className="px-6 py-4">Plan</th>
                        <th className="px-6 py-4">Amount</th>
                        <th className="px-6 py-4">Method</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Transaction ID / Order ID</th>
                        <th className="px-6 py-4">Date & Time</th>
                        <th className="px-6 py-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-300">
                      {payments
                        .filter(item => {
                          const matchesStatus =
                            paymentStatusFilter === 'all' ||
                            item.status.toLowerCase() === paymentStatusFilter;
                          const q = paymentSearchQuery.toLowerCase();
                          const matchesSearch =
                            !q ||
                            (item.user_name && item.user_name.toLowerCase().includes(q)) ||
                            (item.user_email && item.user_email.toLowerCase().includes(q)) ||
                            (item.transaction_id && item.transaction_id.toLowerCase().includes(q)) ||
                            (item.razorpay_order_id && item.razorpay_order_id.toLowerCase().includes(q)) ||
                            (item.razorpay_payment_id && item.razorpay_payment_id.toLowerCase().includes(q));
                          return matchesStatus && matchesSearch;
                        })
                        .map(item => (
                          <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20 transition-colors">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-extrabold text-slate-900 dark:text-white text-xs">{item.user_name}</p>
                                <p className="text-[10px] text-slate-400 mt-0.5">{item.user_email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs font-bold text-slate-800 dark:text-slate-200">
                              {item.plan_details?.name || item.plan?.name || 'Standard Plan'}
                            </td>
                            <td className="px-6 py-4 font-black text-slate-900 dark:text-white">
                              ₹{parseFloat(item.amount).toFixed(2)}
                            </td>
                            <td className="px-6 py-4 uppercase font-bold text-[10px] text-slate-500">
                              {item.payment_method || 'Razorpay'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2.5 py-1 text-[10px] font-black uppercase rounded-full tracking-wider border ${
                                item.status === 'COMPLETED'
                                  ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-800'
                                  : item.status === 'PENDING'
                                  ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-800'
                                  : 'bg-red-50 text-red-750 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-800'
                              }`}>
                                {item.status === 'COMPLETED' ? 'Successful' : item.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 font-mono text-[10px] text-slate-500 max-w-[150px] truncate" title={item.transaction_id || item.razorpay_payment_id || item.razorpay_order_id}>
                              {item.transaction_id || item.razorpay_payment_id || item.razorpay_order_id || 'N/A'}
                            </td>
                            <td className="px-6 py-4 text-slate-550 dark:text-slate-400">
                              {new Date(item.created_at).toLocaleString()}
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => setSelectedPaymentForReceipt(item)}
                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-650 text-slate-700 dark:text-slate-200 rounded-lg text-[10px] font-black tracking-wider uppercase transition flex items-center gap-1.5"
                              >
                                <FileText className="w-3.5 h-3.5" />
                                Receipt
                              </button>
                            </td>
                          </tr>
                        ))}
                      {payments.length === 0 && (
                        <tr>
                          <td colSpan="8" className="px-6 py-16 text-center text-slate-450 font-bold text-sm">
                            No payment records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals (Simplified for context - existing modal structure remains same but uses new fields) */}

      {/* Payment Receipt / Invoice Modal */}
      {
        selectedPaymentForReceipt && (
          <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all" onClick={() => setSelectedPaymentForReceipt(null)}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl transition-colors"
              onClick={e => e.stopPropagation()}
            >
              {/* Receipt Header */}
              <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 flex justify-between items-center transition-colors">
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-wider">Payment Receipt</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5 font-bold">
                    Receipt #: REC-SUB-{selectedPaymentForReceipt.id}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedPaymentForReceipt(null)}
                  className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-750 text-slate-500 rounded-full transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Receipt Body */}
              <div className="p-8 max-h-[70vh] overflow-y-auto space-y-6 text-slate-850 dark:text-slate-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-extrabold text-sm text-slate-900 dark:text-white uppercase tracking-tight">Billing To</h4>
                    <p className="text-xs font-bold text-slate-800 mt-1">{selectedPaymentForReceipt.user_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{selectedPaymentForReceipt.user_email}</p>
                  </div>
                  <div className="text-right">
                    <h4 className="font-extrabold text-sm text-slate-900 dark:text-white uppercase tracking-tight">Receipt Details</h4>
                    <p className="text-xs text-slate-500 mt-1">Date: {new Date(selectedPaymentForReceipt.created_at).toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Status: <span className="font-extrabold text-emerald-600 uppercase">{selectedPaymentForReceipt.status}</span></p>
                  </div>
                </div>

                <div className="border-t border-b border-slate-100 dark:border-slate-700 py-4 my-4">
                  <div className="flex justify-between font-bold text-xs uppercase text-slate-400 mb-2.5">
                    <span>Description</span>
                    <span>Amount</span>
                  </div>
                  <div className="flex justify-between text-xs font-extrabold text-slate-800 dark:text-white">
                    <span>
                      {selectedPaymentForReceipt.plan_details?.name || 'Standard Plan'} Subscription
                    </span>
                    <span>₹{(parseFloat(selectedPaymentForReceipt.amount) / 1.18).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>CGST (9%)</span>
                    <span>₹{(parseFloat(selectedPaymentForReceipt.amount) * 0.09 / 1.18).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>SGST (9%)</span>
                    <span>₹{(parseFloat(selectedPaymentForReceipt.amount) * 0.09 / 1.18).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-black text-slate-900 dark:text-white border-t border-slate-150 dark:border-slate-700 pt-3.5 mt-3.5">
                    <span className="text-purple-600">GRAND TOTAL (incl. GST)</span>
                    <span className="text-purple-600">₹{parseFloat(selectedPaymentForReceipt.amount).toFixed(2)}</span>
                  </div>
                </div>

                {/* Metadata details */}
                <div className="p-4 bg-slate-50 dark:bg-slate-900/30 rounded-2xl border border-slate-150 dark:border-slate-700/60 space-y-2.5 font-mono text-[10px]">
                  <p className="font-bold text-xs text-slate-800 dark:text-white uppercase tracking-widest font-sans mb-1">Transaction Metadata</p>
                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-slate-500">Payment ID:</span>
                    <span className="col-span-2 text-slate-800 dark:text-slate-200 truncate">{selectedPaymentForReceipt.razorpay_payment_id || 'N/A'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-slate-500">Order ID:</span>
                    <span className="col-span-2 text-slate-800 dark:text-slate-200 truncate">{selectedPaymentForReceipt.razorpay_order_id || 'N/A'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-slate-500">UTR / Ref ID:</span>
                    <span className="col-span-2 text-slate-800 dark:text-slate-200 truncate">{selectedPaymentForReceipt.transaction_id || 'N/A'}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-slate-500">Method:</span>
                    <span className="col-span-2 text-slate-800 dark:text-slate-200 uppercase">{selectedPaymentForReceipt.payment_method || 'Razorpay'}</span>
                  </div>
                </div>
              </div>

              {/* Receipt Footer */}
              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex justify-between items-center transition-colors">
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-200 rounded-lg text-xs font-bold transition flex items-center gap-1.5"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Print Receipt
                </button>
                <button
                  onClick={() => setSelectedPaymentForReceipt(null)}
                  className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold transition-all"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </div>
        )
      }

      {/* View Details Modal */}
      {
        selectedPlanDetails && (
          <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all" onClick={() => setSelectedPlanDetails(null)}>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl transition-colors"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 transition-colors">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">{selectedPlanDetails.name}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm">{selectedPlanDetails.description}</p>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4">
                <h4 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2">Included Features</h4>
                <div className="space-y-3">
                  {Array.from(new Set(Array.isArray(selectedPlanDetails.features) ? selectedPlanDetails.features : [])).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700/50 transition-colors">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-500" />
                      </div>
                      <span className="text-slate-700 dark:text-slate-300 text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex justify-end transition-colors">
                <button
                  onClick={() => setSelectedPlanDetails(null)}
                  className="px-6 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white rounded-lg transition-all font-semibold"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )
      }

      {/* Edit Plan Modal */}
      {
        showEditModal && editingPlan && (
          <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl transition-colors"
            >
              <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 transition-colors">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Edit Plan: {editingPlan.name}</h3>
              </div>

              <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Plan Name</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Price (₹)</label>
                    <input
                      type="number"
                      value={editForm.price}
                      onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Duration (days)</label>
                    <input
                      type="number"
                      value={editForm.duration_days}
                      onChange={(e) => setEditForm({ ...editForm, duration_days: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Max Staff Users</label>
                    <input
                      type="number"
                      value={editForm.max_staff_users}
                      onChange={(e) => setEditForm({ ...editForm, max_staff_users: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Invoice Limit</label>
                    <input
                      type="text"
                      value={editForm.invoice_limit}
                      onChange={(e) => setEditForm({ ...editForm, invoice_limit: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Product Limit</label>
                    <input
                      type="text"
                      value={editForm.product_limit}
                      onChange={(e) => setEditForm({ ...editForm, product_limit: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Customer Limit</label>
                    <input
                      type="text"
                      value={editForm.customer_limit}
                      onChange={(e) => setEditForm({ ...editForm, customer_limit: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none resize-none transition-all"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Features (One per line)</label>
                  <textarea
                    value={editForm.features}
                    onChange={(e) => setEditForm({ ...editForm, features: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none resize-none transition-all"
                    rows="5"
                    placeholder="e.g.&#10;5 Staff Accounts&#10;300 Invoices&#10;Daily Sales Report"
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex gap-3 justify-end transition-colors">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white rounded-lg transition-all font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePlan}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all font-semibold shadow-lg shadow-purple-500/20"
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          </div>
        )
      }

      {/* Create Plan Modal */}
      {
        showCreateModal && (
          <div className="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl transition-colors"
            >
              <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50 transition-colors">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Create New Plan</h3>
              </div>

              <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Plan Name *</label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="e.g., Premium Plan"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Price (₹) *</label>
                    <input
                      type="number"
                      value={createForm.price}
                      onChange={(e) => setCreateForm({ ...createForm, price: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Duration (days) *</label>
                    <input
                      type="number"
                      value={createForm.duration_days}
                      onChange={(e) => setCreateForm({ ...createForm, duration_days: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="30"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Max Staff Users</label>
                    <input
                      type="number"
                      value={createForm.max_staff_users}
                      onChange={(e) => setCreateForm({ ...createForm, max_staff_users: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="3"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Invoice Limit</label>
                    <input
                      type="text"
                      value={createForm.invoice_limit}
                      onChange={(e) => setCreateForm({ ...createForm, invoice_limit: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Product Limit</label>
                    <input
                      type="text"
                      value={createForm.product_limit}
                      onChange={(e) => setCreateForm({ ...createForm, product_limit: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="e.g. 200 or Unlimited"
                    />
                  </div>

                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Customer Limit</label>
                    <input
                      type="text"
                      value={createForm.customer_limit}
                      onChange={(e) => setCreateForm({ ...createForm, customer_limit: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="e.g. 20 or Unlimited"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none resize-none transition-all"
                    rows="3"
                    placeholder="Best for..."
                  />
                </div>

                <div>
                  <label className="block text-slate-600 dark:text-slate-400 text-sm font-semibold mb-2">Features (One per line)</label>
                  <textarea
                    value={createForm.features}
                    onChange={(e) => setCreateForm({ ...createForm, features: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none resize-none transition-all"
                    rows="5"
                    placeholder="e.g.&#10;5 Staff Accounts&#10;300 Invoices&#10;Daily Sales Report"
                  />
                </div>
              </div>

              <div className="p-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex gap-3 justify-end transition-colors">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-white rounded-lg transition-all font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePlan}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all font-semibold shadow-lg shadow-purple-500/20"
                >
                  Create Plan
                </button>
              </div>
            </motion.div>
          </div>
        )
      }
    </div>
  );
}
